/**
 * Product Images — horizontal thumbnail gallery parsed from JSON field.
 * For TikTok: editable gallery with upload, reorder, delete.
 * For other channels: read-only lightbox.
 */

import { useState, useCallback, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  X,
  Plus,
  Loader2,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragStartEvent, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig, extractImageUrls } from "./shared";
import {
  useTikTokEditStore,
  selectFieldValue,
  selectIsReadOnly,
} from "../stores/useTikTokEditStore";
import {
  tiktokProductApi,
  validateTikTokImage,
  type TikTokImage,
} from "../api/tiktokProductApi";
import { cn } from "@/lib/utils";

const MAX_IMAGES = 9;

// ── Read-only gallery (Lazada and other channels) ─────────────────────

function ReadOnlyGallery({ images }: { images: string[] }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const navigateImage = useCallback(
    (direction: 1 | -1) => {
      if (lightboxIndex === null) return;
      setLightboxIndex(
        (prev) => ((prev ?? 0) + direction + images.length) % images.length,
      );
    },
    [lightboxIndex, images],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") navigateImage(-1);
      else if (e.key === "ArrowRight") navigateImage(1);
    },
    [navigateImage],
  );

  return (
    <>
      {images.length === 1 ? (
        <div className="p-4">
          <button
            type="button"
            className="w-full cursor-pointer"
            onClick={() => setLightboxIndex(0)}
          >
            <img
              src={images[0]}
              alt="Product"
              className="w-full aspect-[4/3] rounded object-cover"
              loading="lazy"
            />
          </button>
        </div>
      ) : (
        <div className="flex gap-3 p-4 overflow-x-auto">
          {images.map((url, i) => (
            <button
              key={i}
              type="button"
              className="cursor-pointer shrink-0"
              onClick={() => setLightboxIndex(i)}
            >
              <img
                src={url}
                alt={`Product ${i + 1}`}
                className="w-20 h-20 rounded object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      <Dialog
        open={lightboxIndex !== null}
        onOpenChange={(open) => !open && setLightboxIndex(null)}
      >
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] bg-transparent shadow-none border-0 p-0 rounded-none"
          onKeyDown={handleKeyDown}
        >
          <DialogTitle className="sr-only">
            Product Image {(lightboxIndex ?? 0) + 1}
          </DialogTitle>
          <img
            src={images[lightboxIndex ?? 0]}
            alt={`Product ${(lightboxIndex ?? 0) + 1}`}
            className="max-w-full max-h-[85vh] object-contain mx-auto rounded-sm"
          />
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => navigateImage(-1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={() => navigateImage(1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Sortable image item for drag-reorder ──────────────────────────────

interface TikTokSortableImageProps {
  image: TikTokImage;
  index: number;
  isEditing: boolean;
  isLastImage: boolean;
  onDelete: (index: number) => void;
  onClick: (index: number) => void;
}

function TikTokSortableImage({
  image,
  index,
  isEditing,
  isLastImage,
  onDelete,
  onClick,
}: TikTokSortableImageProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: image.uri || `img-${index}`,
    disabled: !isEditing,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition || "transform 200ms ease",
  };

  return (
    <div
      ref={setNodeRef}
      style={isEditing ? style : undefined}
      className={cn(
        "relative aspect-square rounded overflow-hidden bg-muted/30",
        isDragging && "opacity-0",
        isEditing && "touch-none select-none",
      )}
    >
      <img
        src={image.url}
        alt={`Product ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none"
        loading="lazy"
      />

      {isEditing && (
        <>
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="absolute top-2 left-2 p-1.5 rounded-xs bg-black/50 backdrop-blur-sm text-white/90 cursor-grab active:cursor-grabbing z-10"
          >
            <GripVertical className="h-4 w-4" />
          </div>

          {/* Delete button — disabled on last image */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
            disabled={isLastImage}
            className={cn(
              "absolute top-2 right-2 p-1.5 rounded-full z-10 transition-colors",
              isLastImage
                ? "bg-black/20 text-white/40 cursor-not-allowed"
                : "bg-black/50 backdrop-blur-sm text-white hover:bg-red-500/80",
            )}
          >
            <X className="h-4 w-4" />
          </button>

          {/* Index badge */}
          <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded-md bg-black/50 backdrop-blur-sm text-white text-xs font-medium">
            {index + 1}
          </div>
        </>
      )}

      {/* In view mode, clicking opens lightbox */}
      {!isEditing && (
        <button
          type="button"
          className="absolute inset-0 cursor-pointer"
          onClick={() => onClick(index)}
        />
      )}
    </div>
  );
}

// ── Upload placeholder (shown during upload) ──────────────────────────

interface UploadingImage {
  id: string;
  status: "uploading" | "error";
  file: File;
  previewUrl: string;
}

function UploadPlaceholder({
  item,
  onRetry,
  onRemove,
}: {
  item: UploadingImage;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="relative aspect-square rounded overflow-hidden bg-muted/50 border-2 border-dashed border-border/60">
      <img
        src={item.previewUrl}
        alt="Uploading"
        className="w-full h-full object-cover opacity-50"
      />
      {item.status === "uploading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Loader2 className="h-8 w-8 text-white animate-spin" />
        </div>
      )}
      {item.status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 gap-2">
          <button
            type="button"
            onClick={() => onRetry(item.id)}
            className="p-2 rounded-full bg-white/90 text-red-500 hover:bg-white transition-colors"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="text-white/80 text-xs underline hover:text-white"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// ── TikTok editable gallery ───────────────────────────────────────────

function TikTokImageGallery({ formData: _formData }: { formData: Record<string, any> }) {
  const isReadOnly = useTikTokEditStore(selectIsReadOnly);
  const [isEditing, setIsEditing] = useState(false);
  const effectiveEditing = isEditing && !isReadOnly;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState<UploadingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = useTikTokEditStore(selectFieldValue("main_images")) as TikTokImage[] || [];
  const connectionName = useTikTokEditStore((s) => s.connectionName);
  const setField = useTikTokEditStore((s) => s.setField);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  const imageCount = images.length;
  const canAdd = imageCount + uploading.length < MAX_IMAGES;

  // ── Upload flow ──────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || !connectionName) return;

      const filesToUpload = Array.from(files).slice(
        0,
        MAX_IMAGES - imageCount - uploading.length,
      );

      for (const file of filesToUpload) {
        const error = validateTikTokImage(file);
        if (error) {
          // Show inline — no toast system needed
          console.warn(`TikTok image validation: ${error}`);
          continue;
        }

        const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const previewUrl = URL.createObjectURL(file);

        setUploading((prev) => [
          ...prev,
          { id, status: "uploading", file, previewUrl },
        ]);

        try {
          const result = await tiktokProductApi.uploadImage(
            connectionName,
            file,
          );
          // Success: add to images array and remove from uploading
          const newImages = [
            ...useTikTokEditStore.getState().dirtyFields.main_images ??
              useTikTokEditStore.getState().originalValues.main_images ??
              [],
            result,
          ];
          setField("main_images", newImages);
          setUploading((prev) => prev.filter((u) => u.id !== id));
          URL.revokeObjectURL(previewUrl);
        } catch {
          // Error: mark as failed, keep placeholder for retry
          setUploading((prev) =>
            prev.map((u) => (u.id === id ? { ...u, status: "error" as const } : u)),
          );
        }
      }
    },
    [connectionName, imageCount, uploading.length, setField],
  );

  const handleRetry = useCallback(
    async (id: string) => {
      const item = uploading.find((u) => u.id === id);
      if (!item || !connectionName) return;

      setUploading((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status: "uploading" as const } : u)),
      );

      try {
        const result = await tiktokProductApi.uploadImage(
          connectionName,
          item.file,
        );
        const currentImages =
          useTikTokEditStore.getState().dirtyFields.main_images ??
          useTikTokEditStore.getState().originalValues.main_images ??
          [];
        setField("main_images", [...currentImages, result]);
        setUploading((prev) => prev.filter((u) => u.id !== id));
        URL.revokeObjectURL(item.previewUrl);
      } catch {
        setUploading((prev) =>
          prev.map((u) => (u.id === id ? { ...u, status: "error" as const } : u)),
        );
      }
    },
    [connectionName, uploading, setField],
  );

  const handleRemoveUploading = useCallback(
    (id: string) => {
      setUploading((prev) => {
        const item = prev.find((u) => u.id === id);
        if (item) URL.revokeObjectURL(item.previewUrl);
        return prev.filter((u) => u.id !== id);
      });
    },
    [],
  );

  // ── Delete ───────────────────────────────────────────────────────

  const handleDelete = useCallback(
    (index: number) => {
      if (images.length <= 1) return; // Min 1 image
      const newImages = images.filter((_, i) => i !== index);
      setField("main_images", newImages);
    },
    [images, setField],
  );

  // ── Drag reorder ─────────────────────────────────────────────────

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) return;

      const oldIndex = images.findIndex(
        (img, i) => (img.uri || `img-${i}`) === active.id,
      );
      const newIndex = images.findIndex(
        (img, i) => (img.uri || `img-${i}`) === over.id,
      );
      if (oldIndex === -1 || newIndex === -1) return;

      const newImages = arrayMove(images, oldIndex, newIndex);
      setField("main_images", newImages);
    },
    [images, setField],
  );

  const activeImage = activeId
    ? images.find((img, i) => (img.uri || `img-${i}`) === activeId)
    : null;

  // ── Lightbox navigation ──────────────────────────────────────────

  const navigateImage = useCallback(
    (direction: 1 | -1) => {
      if (lightboxIndex === null) return;
      setLightboxIndex(
        (prev) =>
          ((prev ?? 0) + direction + images.length) % images.length,
      );
    },
    [lightboxIndex, images.length],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") navigateImage(-1);
      else if (e.key === "ArrowRight") navigateImage(1);
    },
    [navigateImage],
  );

  // ── Empty state ──────────────────────────────────────────────────

  if (imageCount === 0 && uploading.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
          Product Images
        </h3>
        <div className="rounded-2xl overflow-hidden bg-card p-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full aspect-[4/3] rounded border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary/40 hover:text-primary/60 transition-colors cursor-pointer"
          >
            <Plus className="h-8 w-8" />
            <span className="text-sm">Add product images</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between pl-4 pr-4 mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Product Images
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {imageCount}/{MAX_IMAGES} images
          </span>
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              className={cn(
                "p-1.5 rounded-sm transition-colors",
                effectiveEditing
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden bg-card">
        {effectiveEditing ? (
          /* ── Edit mode: grid with drag reorder ── */
          <div className="p-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={images.map((img, i) => img.uri || `img-${i}`)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {images.map((image, index) => (
                    <TikTokSortableImage
                      key={image.uri || `img-${index}`}
                      image={image}
                      index={index}
                      isEditing={true}
                      isLastImage={images.length <= 1}
                      onDelete={handleDelete}
                      onClick={setLightboxIndex}
                    />
                  ))}

                  {/* Upload placeholders */}
                  {uploading.map((item) => (
                    <UploadPlaceholder
                      key={item.id}
                      item={item}
                      onRetry={handleRetry}
                      onRemove={handleRemoveUploading}
                    />
                  ))}

                  {/* Add image tile */}
                  {canAdd && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary/40 hover:text-primary/60 transition-colors cursor-pointer"
                    >
                      <Plus className="h-6 w-6" />
                      <span className="text-xs">Add</span>
                    </button>
                  )}
                </div>
              </SortableContext>

              <DragOverlay dropAnimation={null}>
                {activeImage ? (
                  <div className="w-[120px] aspect-square rounded overflow-hidden border-2 border-primary/60 ring-4 ring-primary/20 shadow-2xl scale-105">
                    <img
                      src={activeImage.url}
                      alt="Dragging"
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFileSelect(e.target.files);
                // Reset so same file can be re-selected
                e.target.value = "";
              }}
            />
          </div>
        ) : (
          /* ── View mode: horizontal scroll + lightbox ── */
          <>
            {images.length === 1 ? (
              <div className="p-4">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => setLightboxIndex(0)}
                >
                  <img
                    src={images[0].url}
                    alt="Product"
                    className="w-full aspect-[4/3] rounded object-cover"
                    loading="lazy"
                  />
                </button>
              </div>
            ) : (
              <div className="flex gap-3 p-4 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={img.uri || i}
                    type="button"
                    className="cursor-pointer shrink-0"
                    onClick={() => setLightboxIndex(i)}
                  >
                    <img
                      src={img.url}
                      alt={`Product ${i + 1}`}
                      className="w-20 h-20 rounded object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox (view mode) */}
      <Dialog
        open={lightboxIndex !== null && !effectiveEditing}
        onOpenChange={(open) => !open && setLightboxIndex(null)}
      >
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] bg-transparent shadow-none border-0 p-0 rounded-none"
          onKeyDown={handleKeyDown}
        >
          <DialogTitle className="sr-only">
            Product Image {(lightboxIndex ?? 0) + 1}
          </DialogTitle>
          <img
            src={images[lightboxIndex ?? 0]?.url}
            alt={`Product ${(lightboxIndex ?? 0) + 1}`}
            className="max-w-full max-h-[85vh] object-contain mx-auto rounded-sm"
          />
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => navigateImage(-1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={() => navigateImage(1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Main widget export ────────────────────────────────────────────────

export function MarketplaceImagesWidget({
  section,
  formData,
}: ExperienceWidgetProps) {
  const config = getChannelConfig(section.description || "");
  const channel = section.description || "";
  const isTikTok = channel === "TikTok Shop";

  // TikTok: editable gallery with upload/reorder/delete
  if (isTikTok) {
    return <TikTokImageGallery formData={formData} />;
  }

  // Other channels: read-only lightbox
  const images = extractImageUrls(formData?.[config.images]);
  if (!images || images.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Product Images
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        <ReadOnlyGallery images={images} />
      </div>
    </div>
  );
}
